var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx } from "react/jsx-runtime";
import { useState, useMemo, useCallback } from 'react';
import { useAccountHistory } from '../../hooks/useAccountHistory';
import { getIcon } from '../../utils/getIcon';
import { exportToCsv, createTransactionCsvData } from '../../utils/csvUtils';
import { formatTimestamp } from '../../utils/dateUtils';
import { getDefaultFilterOptions, getActionColor } from '../../utils/transactionUtils';
import { getTokenConfig } from '../../utils/tokenUtils';
import { TransactionHistoryTable, } from 'ui-kit';
export const getExplorerLink = (digest, network) => {
    const net = network === 'testnet' ? 'testnet' : 'mainnet';
    return `https://suiscan.xyz/${net}/tx/${digest}`;
};
const TransactionHistory = ({ positionCapId, isDark }) => {
    const { transactions, loading, loadingMore, hasMoreData, loadMore, syncProgress, fetchAllTransactions, } = useAccountHistory(positionCapId, 4, 4);
    const [exportingCsv, setExportingCsv] = useState(false);
    const transactionIcons = {
        download: isDark ? getIcon('downloadLight') : getIcon('downloadDark'),
        externalLink: isDark ? getIcon('externalLinkLight') : getIcon('externalIconWhite'),
        arrowUp: isDark ? getIcon('darkThemeUpArrow') : getIcon('ArrowUpBlack'),
        arrowDown: isDark ? getIcon('ArrowDownWhite') : getIcon('ArrowDownBlack'),
        search: isDark ? getIcon('searchIcon') : getIcon('searchIconDarkTheme'),
        clear: isDark ? getIcon('close') : getIcon('darkThemeCloseIcon'),
        checkmark: isDark ? getIcon('checkmarkLight') : getIcon('checkmarkDark'),
        triangleArrow: isDark ? getIcon('triangleArrowLight') : getIcon('triangleArrowDark'),
    };
    const [filters, setFilters] = useState({
        activeFilters: ['ALL'],
        searchQuery: '',
    });
    const [sortConfig, setSortConfig] = useState({
        key: 'timestamp',
        direction: 'desc',
    });
    const handleFilterChange = (newFilters) => {
        setFilters((prev) => (Object.assign(Object.assign({}, prev), { activeFilters: newFilters })));
    };
    const handleSort = (key) => {
        setSortConfig((prev) => ({
            key,
            direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc',
        }));
    };
    const handleExportCsv = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!positionCapId || exportingCsv)
            return;
        setExportingCsv(true);
        try {
            // Fetch all transactions in the background
            const allTransactions = yield fetchAllTransactions();
            if (allTransactions.length === 0) {
                console.warn('No transactions to export');
                return;
            }
            // Export all transactions
            const { headers, data, filename } = createTransactionCsvData(allTransactions, positionCapId);
            exportToCsv({ headers, data, filename });
        }
        catch (error) {
            console.error('Failed to export CSV:', error);
        }
        finally {
            setExportingCsv(false);
        }
    });
    const customFilterOptions = useMemo(() => {
        const options = getDefaultFilterOptions();
        return options.map((opt) => ({
            label: opt.label,
            value: opt.value,
        }));
    }, []);
    // getTokenConfig no longer needs network parameter
    const getTokenConfigForTable = useCallback((coinType) => getTokenConfig(coinType), []);
    if (!positionCapId) {
        return (_jsx("div", { className: "text-center py-12", children: _jsx("div", { className: "theme-text-lndSecondary text-lg", children: "Connect your wallet to view transaction history." }) }));
    }
    return (_jsx("div", { className: `theme-bg-lndPrimary sm:rounded-3xl rounded-xl shadow-sm`, children: _jsx(TransactionHistoryTable, { transactions: transactions, loading: loading, loadingMore: loadingMore, hasMoreData: hasMoreData, onLoadMore: loadMore, onExportCsv: handleExportCsv, exportingCsv: exportingCsv, filters: filters, onFilterChange: handleFilterChange, sortConfig: sortConfig, onSort: handleSort, filterOptions: customFilterOptions, allFilterValue: 'ALL', network: 'mainnet', icons: transactionIcons, getTokenConfig: getTokenConfigForTable, formatTimestamp: formatTimestamp, getExplorerLink: getExplorerLink, getActionColor: getActionColor, syncProgress: syncProgress }) }));
};
export default TransactionHistory;
